import React, { useEffect, useRef, useState } from 'react';
import { IdleTimeOutModal } from './SessionModal';
import { AppStorage } from '../Service/storage-service';

const IdleTimeOutHandler = (props) => {
    // Include touch so mobile taps count as activity.
    const events = ['click', 'keydown', 'mousedown', 'touchstart', 'scroll', 'load'];

    const timerRef = useRef(null);
    // Wall-clock time of the last user activity. Anchoring the timer to this
    // (instead of a plain setTimeout) is what makes the idle window survive a
    // mobile / bfcache background freeze: setTimeout is frozen while the tab is
    // hidden, so on resume we recompute the true remaining time from this ref.
    const lastActivityRef = useRef(Date.now());
    const isLogoutRef = useRef(false);

    const [showModal, setShowModal] = useState(false);

    const getIdleWindowMs = () => {
        const appStorageTimeOut = AppStorage.getPortalSetting()?.portalSettingTimeOut;
        const userMinutes = appStorageTimeOut != null ? appStorageTimeOut - 1 : 59;
        const ms = userMinutes * 60000;
        return ms > 0 ? ms : 300000;
    };

    const logoutNow = () => {
        isLogoutRef.current = true;
        if (timerRef.current) clearTimeout(timerRef.current);
        setShowModal(false);
        props.onLogout();
        props.Logout();
    };

    // (Re)arm the idle timer for the TRUE remaining time, measured from the last
    // activity. If the window already elapsed (e.g. the tab was backgrounded past
    // it) log out immediately instead of waiting out a stale, frozen timeout.
    const startTimer = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (isLogoutRef.current) return;

        const remaining = getIdleWindowMs() - (Date.now() - lastActivityRef.current);
        if (remaining <= 0) {
            logoutNow();
            return;
        }
        timerRef.current = setTimeout(() => {
            if (isLogoutRef.current) return;
            props.onIdle();
            setShowModal(true);
        }, remaining);
    };

    const eventHandler = () => {
        if (isLogoutRef.current) return;
        lastActivityRef.current = Date.now();
        props.onActive();
        startTimer();
    };

    // Mobile browsers freeze timers while backgrounded, and bfcache can restore a
    // fully frozen page. Re-evaluate against wall-clock on resume.
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') startTimer();
    };
    const handlePageShow = (event) => {
        if (event.persisted) startTimer();
    };

    useEffect(() => {
        events.forEach((eventName) => window.addEventListener(eventName, eventHandler));
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);

        lastActivityRef.current = Date.now();
        startTimer();

        return () => {
            events.forEach((eventName) => window.removeEventListener(eventName, eventHandler));
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleContinueSession = () => {
        setShowModal(false);
        isLogoutRef.current = false;
        lastActivityRef.current = Date.now();
        startTimer();
    };

    return (
        <div>
            <IdleTimeOutModal showModal={showModal} handleContinue={handleContinueSession} handleLogout={logoutNow} />
        </div>
    );
};

export default IdleTimeOutHandler;
